import { api } from '../data/api.ts';

/**
 * Aufnahme im Browser, Erkennung über den eigenen Server
 * (docs/KONZEPT-ERINNERUNGEN.md, Abschnitt 4.1).
 *
 * Opus in WebM ist auf allen Zielbrowsern vorhanden und klein genug, dass auch eine
 * mobile Verbindung reicht — ein diktierter Satz sind wenige Kilobyte.
 */

export type DictateState = 'idle' | 'recording' | 'working';

/** Safari meldet WebM/Opus nicht überall; MP4 ist dort der gangbare Weg. */
function pickMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export class Dictation {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;

  get active(): boolean {
    return this.recorder !== null;
  }

  async start(): Promise<void> {
    if (this.recorder) return;
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = pickMimeType();
    this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.chunks = [];
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.start();
  }

  /** Beendet die Aufnahme und gibt den erkannten Text zurück. */
  async stop(): Promise<string> {
    const recorder = this.recorder;
    if (!recorder) return '';

    const finished = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await finished;

    // Das Mikrofon freigeben — sonst bleibt die Aufnahmeanzeige des Browsers stehen.
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.recorder = null;
    this.stream = null;

    const audio = new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' });
    this.chunks = [];
    if (audio.size === 0) return '';

    const { text } = await api.transcribe(audio);
    return text;
  }

  /** Abbrechen ohne Erkennung — etwa wenn der Bereich verlassen wird. */
  cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.recorder = null;
    this.stream = null;
    this.chunks = [];
  }
}

/** Ob Aufnahme und Erkennung überhaupt zur Verfügung stehen. */
export async function dictationAvailable(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return false;
  try {
    const { available } = await api.transcribeStatus();
    return available;
  } catch {
    return false;
  }
}
